// Hook for fetching and caching intel about a target province (SF-55)

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMyIntel } from '../api/intel';
import { parseIntelData } from '../types/intel';
import type { IntelReportDto, CrystalBallData, SpyOnMilitaryData, SurveyData, SpyOnScienceData, SpyOnHomeDefenseData, SpyOnThroneData } from '../types/intel';

export interface TargetIntelData {
  cbReport: IntelReportDto | null;
  somReport: IntelReportDto | null;
  surveyReport: IntelReportDto | null;
  sosReport: IntelReportDto | null;
  sohdReport: IntelReportDto | null;
  // SF-174: Spy on Throne — newly fetched by this hook (needed for the Massacre panel's
  // thieves+wizards fields; not previously fetched from War Room at all).
  sotReport: IntelReportDto | null;
  cbData: CrystalBallData | null;
  somData: SpyOnMilitaryData | null;
  surveyData: SurveyData | null;
  sosData: SpyOnScienceData | null;
  sohdData: SpyOnHomeDefenseData | null;
  sotData: SpyOnThroneData | null;
  isLoading: boolean;
  error: string | null;
  refreshIntel: () => Promise<void>;
}

export function useTargetIntel(targetProvinceId: string | null): TargetIntelData {
  const { worldId } = useAuth();
  const [cbReport, setCbReport] = useState<IntelReportDto | null>(null);
  const [somReport, setSomReport] = useState<IntelReportDto | null>(null);
  const [surveyReport, setSurveyReport] = useState<IntelReportDto | null>(null);
  const [sosReport, setSosReport] = useState<IntelReportDto | null>(null);
  const [sohdReport, setSohdReport] = useState<IntelReportDto | null>(null);
  const [sotReport, setSotReport] = useState<IntelReportDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track current target to avoid stale responses
  const currentTargetRef = useRef<string | null>(null);

  const fetchIntel = useCallback(async () => {
    if (!targetProvinceId || !worldId) {
      setCbReport(null);
      setSomReport(null);
      setSurveyReport(null);
      setSosReport(null);
      setSohdReport(null);
      setSotReport(null);
      return;
    }

    currentTargetRef.current = targetProvinceId;
    setIsLoading(true);
    setError(null);

    try {
      const response = await getMyIntel(worldId, {
        target: targetProvinceId,
        size: 100,
      });

      // Skip update if target changed during fetch
      if (currentTargetRef.current !== targetProvinceId) return;

      // Find most recent report of each type
      const reports = response.reports;
      const latestCb = findLatestByType(reports, 'CB');
      const latestSom = findLatestByType(reports, 'SOM');
      const latestSurvey = findLatestByType(reports, 'SURVEY');
      const latestSos = findLatestByType(reports, 'SOS');
      const latestSohd = findLatestByType(reports, 'SOHD');
      const latestSot = findLatestByType(reports, 'SOT');

      setCbReport(latestCb);
      setSomReport(latestSom);
      setSurveyReport(latestSurvey);
      setSosReport(latestSos);
      setSohdReport(latestSohd);
      setSotReport(latestSot);
    } catch (err) {
      if (currentTargetRef.current === targetProvinceId) {
        setError(err instanceof Error ? err.message : 'Failed to fetch intel');
      }
    } finally {
      if (currentTargetRef.current === targetProvinceId) {
        setIsLoading(false);
      }
    }
  }, [targetProvinceId, worldId]);

  // Fetch when target changes
  useEffect(() => {
    fetchIntel();
  }, [fetchIntel]);

  // Parse data from reports (memoized to prevent infinite re-render loops)
  const cbData = useMemo(
    () => (cbReport ? (parseIntelData(cbReport).data as CrystalBallData) : null),
    [cbReport]
  );
  const somData = useMemo(
    () => (somReport ? (parseIntelData(somReport).data as SpyOnMilitaryData) : null),
    [somReport]
  );
  const surveyData = useMemo(
    () => (surveyReport ? (parseIntelData(surveyReport).data as SurveyData) : null),
    [surveyReport]
  );
  const sosData = useMemo(
    () => (sosReport ? (parseIntelData(sosReport).data as SpyOnScienceData) : null),
    [sosReport]
  );
  const sohdData = useMemo(
    () => (sohdReport ? (parseIntelData(sohdReport).data as SpyOnHomeDefenseData) : null),
    [sohdReport]
  );
  const sotData = useMemo(
    () => (sotReport ? (parseIntelData(sotReport).data as SpyOnThroneData) : null),
    [sotReport]
  );

  return {
    cbReport,
    somReport,
    surveyReport,
    sosReport,
    sohdReport,
    sotReport,
    cbData,
    somData,
    surveyData,
    sosData,
    sohdData,
    sotData,
    isLoading,
    error,
    refreshIntel: fetchIntel,
  };
}

function findLatestByType(reports: IntelReportDto[], type: string): IntelReportDto | null {
  const typed = reports.filter((r) => r.type === type);
  if (typed.length === 0) return null;
  // Sort by capturedAt descending, return most recent
  typed.sort((a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime());
  return typed[0];
}
